// src/services/itinerarioRender.js
// Itinerario del cliente para el paquete del Cotizador BV (Boa Vista → Manaus → destino):
// UNA opción de día ya elegida, dibujada como línea de tiempo (llegada, hostal, ómnibus,
// vuelo, ómnibus extra). No compara días. El precio es opcional y nunca hay costos ni
// ganancia. Mismas medidas y marca que el orçamento de vuelo (A4, Story, Cuadrado).
import {
  C, F, FORMATOS, rr, font, spaced, wrap, lineaAjustada, shadow, icono, plane, cargarImagen, asegurarFuentes,
  cabeceraLarga, cabeceraCuadrada, dibujarPie, cNotas, alturaNotas, recortarNotas, fmtFechaCorta, fmtReales,
} from './orcamentoRender';
import { diaNum, fmtFecha, sumarDias, minDeHora } from './cotizadorCalc';
import { tx } from './idiomaArte';

const diaSemana = (iso, T) => { const n = diaNum(iso); return n === null ? '' : T.semana[(n + 4) % 7]; }; // el día 0 (1/1/1970) fue jueves
const fechaDia = (iso, T) => `${fmtFecha(iso)} · ${diaSemana(iso, T)}`;
const noites = (n, T) => `${n} ${n === 1 ? T.noche : T.noches}`;

// Día en que llega el vuelo: el mismo, o el siguiente si la hora de llegada es menor que la de salida.
export function llegadaVuelo(vuelo) {
  if (!vuelo?.llega) return null;
  if (typeof vuelo.masDias === 'number') return sumarDias(vuelo.diaIso, vuelo.masDias);
  const s = minDeHora(vuelo.sale); const l = minDeHora(vuelo.llega);
  return s !== null && l !== null && l < s ? sumarDias(vuelo.diaIso, 1) : vuelo.diaIso;
}

// ── Pasos de la línea de tiempo ──────────────────────────────────────
// Cada paso: { icono, etiqueta, principal, detalle }
export function pasosItinerario(d) {
  const T = tx(d.idioma);
  const it = d.itinerario;
  const dest = d.destinoCiudad ? String(d.destinoCiudad).toUpperCase() : 'DESTINO';
  const llegaIso = llegadaVuelo(it.vuelo);
  const llegaTxt = it.vuelo.llega
    ? T.llegada.charAt(0) + T.llegada.slice(1).toLowerCase() + (llegaIso && llegaIso !== it.vuelo.diaIso ? ` ${fmtFecha(llegaIso)}` : '') + ` ${it.vuelo.llega}`
    : '';
  const pasos = [
    { icono: 'pin', etiqueta: T.llegadaBV, principal: fechaDia(it.llegadaBV, T), detalle: it.llegadaBVHora ? T.llegaA('', it.llegadaBVHora) : '' },
    { icono: 'bed', etiqueta: T.hospedaje, principal: noites(it.noches, T), detalle: T.delAl(fmtFecha(it.llegadaBV), fmtFecha(it.bus.saleIso)) },
    {
      icono: 'bus', etiqueta: T.busBVM, principal: T.salidaA(fmtFecha(it.bus.saleIso), it.bus.sale),
      detalle: T.llegaManaus(fmtFecha(it.bus.llegaIso), it.bus.llega),
    },
    {
      icono: 'plane', etiqueta: T.vueloMA(dest), principal: fechaDia(it.vuelo.diaIso, T),
      detalle: `${T.salida.charAt(0)}${T.salida.slice(1).toLowerCase()} ${it.vuelo.sale}${llegaTxt ? `  ·  ${llegaTxt}` : ''}`,
    },
  ];
  (it.extras || []).forEach((e) => {
    pasos.push({
      icono: 'bus', etiqueta: T.busHasta(String(e.ciudad || T.destinoFinal).toUpperCase()),
      principal: e.saleHora ? T.salidaA(e.saleFecha ? fmtFecha(e.saleFecha) : '', e.saleHora) : T.horarioPorConfirmar,
      detalle: e.llegaHora ? T.llegaA(e.llegaFecha ? fmtFecha(e.llegaFecha) : '', e.llegaHora) : '',
    });
  });
  return pasos;
}

const H_PASO = 138; const H_ULTIMO = 112; const GAP = 26;
const altoLinea = (n) => (n - 1) * H_PASO + H_ULTIMO;
const incluye = (d) => { const T = tx(d.idioma); return [T.inc1, T.inc2, T.inc3(d.destinoCiudad)]; };
const alturaPrecio = (d) => Math.max(210, 70 + incluye(d).length * 46 + 20);

// ── Piezas (base k=1; el llamador las escala) ────────────────────────
function cLinea(ctx, pasos, w) {
  const cx = 38; const r = 32;
  pasos.forEach((p, i) => {
    const y = i * H_PASO;
    // conector hacia el siguiente paso
    if (i < pasos.length - 1) {
      ctx.save(); ctx.setLineDash([2, 11]); ctx.lineCap = 'round'; ctx.strokeStyle = C.navy; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(cx, y + 36 + r); ctx.lineTo(cx, y + H_PASO + 36 - r); ctx.stroke(); ctx.restore();
    }
    ctx.fillStyle = i === 0 || i === pasos.length - 1 ? C.orange : C.navy;
    ctx.beginPath(); ctx.arc(cx, y + 36, r, 0, Math.PI * 2); ctx.fill();
    if (p.icono === 'plane') plane(ctx, cx, y + 36, 32, Math.PI / 2, C.white);
    else icono(ctx, p.icono, cx - 16, y + 36 - 16, 32, C.white);

    const x = cx + r + 30; const ancho = w - x;
    font(ctx, 700, 16, F.body); ctx.fillStyle = C.muted;
    const e = lineaAjustada(ctx, p.etiqueta, 700, 16, 11, ancho);
    font(ctx, 700, e.s, F.body); spaced(ctx, e.t, x, y + 20, 2.4);
    const m = lineaAjustada(ctx, p.principal, 800, 38, 22, ancho);
    font(ctx, 800, m.s, F.body); ctx.fillStyle = C.ink; ctx.fillText(m.t, x, y + 20 + 6 + m.s);
    if (p.detalle) {
      const dt = lineaAjustada(ctx, p.detalle, 500, 25, 16, ancho);
      font(ctx, 500, dt.s, F.body); ctx.fillStyle = C.muted; ctx.fillText(dt.t, x, y + 20 + 6 + m.s + 34);
    }
  });
}

function cPrecio(ctx, d, w) {
  const T = tx(d.idioma);
  const H = alturaPrecio(d);
  ctx.save(); shadow(ctx, 30, 12, 0.25);
  const pg = ctx.createLinearGradient(0, 0, w, H);
  pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
  ctx.fillStyle = pg; rr(ctx, 0, 0, w, H, 32); ctx.fill();
  ctx.restore();
  ctx.fillStyle = C.orange; rr(ctx, 0, 36, 8, H - 72, 4); ctx.fill();
  const px = 52; const vx = Math.round(w * 0.46);
  font(ctx, 600, 18, F.body); ctx.fillStyle = 'rgba(255,255,255,0.7)';
  spaced(ctx, T.valorPaquete, px, 62, 3.4);
  const valor = Number(d.precioUsd || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  font(ctx, 700, 44, F.body);
  const wU = ctx.measureText('US$').width + 14;
  const l = lineaAjustada(ctx, valor, 800, 116, 56, vx - px - wU - 30);
  font(ctx, 700, 44, F.body); ctx.fillStyle = C.orangeSoft; ctx.fillText('US$', px, 62 + l.s * 0.92);
  font(ctx, 800, l.s, F.body); ctx.fillStyle = C.white; ctx.fillText(l.t, px + wU, 62 + l.s * 0.92);
  if (d.mostrarReales && d.reales) {
    font(ctx, 500, 22, F.body); ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText(T.oReales(fmtReales(d.reales), String(d.tasa).replace('.', ',')), px, H - 34);
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(vx, 44); ctx.lineTo(vx, H - 44); ctx.stroke();
  font(ctx, 600, 16, F.body); ctx.fillStyle = 'rgba(255,255,255,0.7)';
  spaced(ctx, T.incluye, vx + 36, 50, 3);
  let fy = 98;
  incluye(d).forEach((t) => {
    icono(ctx, 'check', vx + 36, fy - 20, 26, C.orangeSoft);
    const li = lineaAjustada(ctx, t, 600, 22, 16, w - (vx + 78) - 20);
    font(ctx, 600, li.s, F.body); ctx.fillStyle = C.white; ctx.fillText(li.t, vx + 76, fy);
    fy += 46;
  });
}

const textosHero = (d) => {
  const T = tx(d.idioma);
  return {
    etiqueta: d.cliente ? T.itinDe(String(d.cliente).toUpperCase()) : T.itin,
    titulo: String(d.destinoCiudad || T.tuViaje).toUpperCase(),
    pie: T.saliendo,
  };
};

// ── A4 y Story ───────────────────────────────────────────────────────
function dibujarLargo(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const OVER = 60; const FOOT = 150; const GAPF = 30; const HERO_MIN = 560; const HERO_MAX = 860; const PAD = 44; const CARD_X = 40;
  const IW = W - 80 - 96;
  const pasos = pasosItinerario(d);
  const hLinea = altoLinea(pasos.length);
  const hPrecio = d.mostrarPrecio ? alturaPrecio(d) : 0;
  const notas = (d.notas || []).filter(Boolean);

  // La línea de tiempo crece (k > 1) para llenar el espacio cuando hay poco contenido.
  const niveles = [1.5, 1.4, 1.3, 1.2, 1.1, 1, 0.94, 0.88, 0.82, 0.78, 0.74];
  const medir = (k, heroMin) => {
    const cw = IW / k;
    font(ctx, 400, 25, F.body);
    const bloques = notas.map((t) => wrap(ctx, t, cw - 32 - 38 - 24));
    const hNotas = alturaNotas(bloques);
    const fijo = hLinea + (hPrecio ? GAP + hPrecio : 0) + (hNotas ? GAP + hNotas : 0);
    return { k, cw, bloques, hNotas, fijo, sobra: (H - FOOT - GAPF + OVER - heroMin - 2 * PAD) / k - fijo };
  };
  let plan = null;
  [HERO_MIN, 500].forEach((heroMin) => { niveles.forEach((k) => { if (!plan) { const m = medir(k, heroMin); if (m.sobra >= 0) plan = m; } }); });
  let desbordo = false;
  if (!plan) {
    // Ni así cabe: se recortan las observaciones.
    plan = medir(niveles[niveles.length - 1], 500);
    const hMax = Math.max(0, plan.hNotas + plan.sobra);
    const rec = hMax > 70 + 4 + 36 ? recortarNotas(plan.bloques, hMax) : [];
    desbordo = rec.reduce((a, l) => a + l.length, 0) < plan.bloques.reduce((a, l) => a + l.length, 0);
    plan = { ...plan, bloques: rec, hNotas: alturaNotas(rec) };
  }
  const { k, cw, bloques, hNotas } = plan;

  const contenido = (hLinea + (hPrecio ? GAP + hPrecio : 0) + (hNotas ? GAP + hNotas : 0)) * k;
  let cardH = 2 * PAD + contenido;
  let heroH = H - FOOT - GAPF + OVER - cardH;
  let offset = 0;
  if (heroH > HERO_MAX) { cardH += heroH - HERO_MAX; offset = (heroH - HERO_MAX) / 2; heroH = HERO_MAX; }

  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);
  cabeceraLarga(ctx, d, logo, W, heroH, textosHero(d));
  const cardTop = heroH - OVER;
  ctx.save(); shadow(ctx, 50, 18, 0.28);
  ctx.fillStyle = C.white; rr(ctx, CARD_X, cardTop, W - 80, cardH, 42); ctx.fill();
  ctx.restore();

  const x0 = CARD_X + 48; let y = cardTop + PAD + offset;
  const pieza = (alto, fn, gap = 0) => { ctx.save(); ctx.translate(x0, y); ctx.scale(k, k); fn(); ctx.restore(); y += (alto + gap) * k; };
  pieza(hLinea, () => cLinea(ctx, pasos, cw), hPrecio || hNotas ? GAP : 0);
  if (hPrecio) pieza(hPrecio, () => cPrecio(ctx, d, cw), hNotas ? GAP : 0);
  if (hNotas) pieza(hNotas, () => cNotas(ctx, bloques, cw, hNotas, tx(d.idioma).observaciones));
  dibujarPie(ctx, d, W, H - FOOT, FOOT);
  return desbordo;
}

// ── Cuadrado 1080×1080 ───────────────────────────────────────────────
function dibujarCuadrado(ctx, d, logo, formato) {
  const { w: W, h: H } = formato;
  const T = tx(d.idioma);
  ctx.fillStyle = C.paper; ctx.fillRect(0, 0, W, H);
  cabeceraCuadrada(ctx, logo, W, {
    etiqueta: `${d.cliente ? T.itinDe(String(d.cliente).toUpperCase()) : T.itinCorto}  ·  ${T.saliendo}`,
    titulo: String(d.destinoCiudad || T.tuViaje).toUpperCase(),
    idioma: d.idioma,
  });

  const CT = 418; const FOOT = 84; const CB = H - FOOT - 18; const CH = CB - CT;
  ctx.save(); shadow(ctx, 40, 14, 0.28);
  ctx.fillStyle = C.white; rr(ctx, 28, CT, W - 56, CH, 36); ctx.fill();
  ctx.restore();

  const x0 = 28 + 34; const iw = W - 56 - 68; let y = CT + 24;
  const pasos = pasosItinerario(d);
  const hLinea = altoLinea(pasos.length);
  const conPrecio = !!d.mostrarPrecio;
  const PH = 70;
  const notas = (d.notas || []).filter(Boolean);
  const reservaPie = (conPrecio ? PH + 12 : 0) + (notas.length ? 34 : 0);
  const libre = CB - 20 - y - reservaPie;
  const k = Math.max(0.5, Math.min(0.78, libre / hLinea));

  ctx.save(); ctx.translate(x0, y); ctx.scale(k, k); cLinea(ctx, pasos, iw / k); ctx.restore();
  y += hLinea * k + 12;

  if (conPrecio) {
    ctx.save(); ctx.translate(x0, y);
    const pg = ctx.createLinearGradient(0, 0, iw, PH); pg.addColorStop(0, C.navyDeep); pg.addColorStop(1, C.navy);
    ctx.fillStyle = pg; rr(ctx, 0, 0, iw, PH, 20); ctx.fill();
    font(ctx, 700, 28, F.body); ctx.fillStyle = C.orangeSoft; ctx.fillText('US$', 26, 46);
    const valor = Number(d.precioUsd || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
    font(ctx, 800, 44, F.body); ctx.fillStyle = C.white; ctx.fillText(valor, 26 + 66, 50);
    font(ctx, 600, 20, F.body); ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.textAlign = 'right';
    ctx.fillText(d.mostrarReales && d.reales ? T.oRealesCorto(fmtReales(d.reales)) : T.paqueteCompleto, iw - 24, 42); ctx.textAlign = 'left';
    ctx.restore();
    y += PH + 12;
  }
  let desbordo = false;
  if (notas.length) {
    font(ctx, 400, 21, F.body);
    const l = wrap(ctx, notas[0], iw - 36);
    icono(ctx, 'check', x0, y + 2, 18, C.orange); ctx.fillStyle = C.ink;
    ctx.fillText(l.length > 1 || notas.length > 1 ? `${l[0].replace(/[\s.,;:]+$/, '')}…` : l[0], x0 + 28, y + 17);
    desbordo = l.length > 1 || notas.length > 1;
  }
  dibujarPie(ctx, d, W, H - FOOT, FOOT, { compacto: true });
  return desbordo;
}

// ── Función principal ────────────────────────────────────────────────
export async function renderItinerario(d, { formato = 'story', scale, logoUrl = '/logo-principal.png' } = {}) {
  await asegurarFuentes();
  let logo = null;
  try { logo = await cargarImagen(logoUrl); } catch { /* sin logo: se omite el recuadro */ }
  const fm = FORMATOS[formato] || FORMATOS.story;
  const esc = scale || fm.escala;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(fm.w * esc);
  canvas.height = Math.round(fm.h * esc);
  const ctx = canvas.getContext('2d');
  ctx.scale(esc, esc);
  ctx.textBaseline = 'alphabetic';
  canvas.desbordo = fm.id === 'cuadrado' ? dibujarCuadrado(ctx, d, logo, fm) : dibujarLargo(ctx, d, logo, fm);
  return canvas;
}
